import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  OpsPaymentOrderStatus,
  OpsPaymentOrderSummary,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useListPaymentOrders,
  useReconcilePaymentOrder,
} from '../api/payments.ts';
import { Modal } from '../components/Modal.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { INPUT_CLASS, INPUT_CLASS_ON_HIGHEST } from '../styles/forms.ts';

/**
 * `/payments/orders` — operator view over `payment_orders`.
 *
 * Three surfaces:
 *   1. Filters bar — status pills (multi-select), account_id, date range.
 *      Default window is the past 7 days so the page opens on a bounded
 *      view rather than a full-table scan.
 *   2. Table — created / account (linked) / status chip / USD / CNY /
 *      applied rate / pay type / paid at / order id. Row click expands
 *      the detail panel with the raw notify payload + upstream trade
 *      number.
 *   3. Reconcile modal — on pending orders only. POSTs to
 *      `/v1/ops/payments/orders/:id/reconcile`; server handles the
 *      authoritative ZPay re-check + atomic credit batch. The modal
 *      shows the current order snapshot so the operator double-checks
 *      the amount before confirming.
 *
 * Amount display rule (per CLAUDE.md): internal state stays in integer
 * cents; render-time division is localised to the formatter helpers
 * below. The `/ 100` exists for display only — server never consumes
 * it back.
 */

const ALL_STATUSES: readonly OpsPaymentOrderStatus[] = [
  'pending',
  'paid',
  'expired',
  'failed',
  'refunded',
];

const STATUS_LABEL_KEY: Record<OpsPaymentOrderStatus, I18nKey> = {
  pending: 'payments_orders.status.pending',
  paid: 'payments_orders.status.paid',
  expired: 'payments_orders.status.expired',
  failed: 'payments_orders.status.failed',
  refunded: 'payments_orders.status.refunded',
};

type StatusChipVariant = 'ok' | 'warning' | 'error' | 'neutral';

const STATUS_VARIANT: Record<OpsPaymentOrderStatus, StatusChipVariant> = {
  pending: 'neutral',
  paid: 'ok',
  expired: 'warning',
  failed: 'error',
  refunded: 'warning',
};

const DEFAULT_WINDOW_DAYS = 7;
const PAGE_LIMIT = 50;

function formatUsdCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatCnyCents(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

function formatRate(rate: number): string {
  return rate.toFixed(4);
}

function formatTimestamp(iso: string | null): string {
  if (iso === null) return '—';
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  return new Date(parsed).toISOString().replace('T', ' ').replace('Z', 'Z');
}

function truncateId(value: string, head = 10, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function isoDateDaysAgo(days: number): string {
  const ms = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

export function PaymentsOrdersPage(): JSX.Element {
  const t = useT();
  // ── Filter state ──────────────────────────────────────────────────────
  // Status starts as empty (== "All") so the page opens wide; clicking
  // a pill toggles it in/out of the set. The `from` default of 7 days
  // ago caps the initial query window.
  const [selectedStatuses, setSelectedStatuses] = useState<
    readonly OpsPaymentOrderStatus[]
  >([]);
  const [accountIdInput, setAccountIdInput] = useState('');
  const [fromInput, setFromInput] = useState<string>(isoDateDaysAgo(DEFAULT_WINDOW_DAYS));
  const [toInput, setToInput] = useState<string>('');
  // Expanded row state — only one row expanded at a time.
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  // Reconcile modal state — null when closed.
  const [reconcileTarget, setReconcileTarget] =
    useState<OpsPaymentOrderSummary | null>(null);

  const query = useMemo(
    () => ({
      status: selectedStatuses,
      accountId: accountIdInput.trim().length > 0 ? accountIdInput.trim() : undefined,
      from: fromInput.trim().length > 0 ? fromInput.trim() : undefined,
      to: toInput.trim().length > 0 ? toInput.trim() : undefined,
      limit: PAGE_LIMIT,
    }),
    [selectedStatuses, accountIdInput, fromInput, toInput],
  );

  const list = useListPaymentOrders(query);

  // Flatten infinite query pages into a single list.
  const orders = useMemo<readonly OpsPaymentOrderSummary[]>(
    () => list.data?.pages.flatMap((p) => p.orders) ?? [],
    [list.data],
  );

  function toggleStatus(s: OpsPaymentOrderStatus): void {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s],
    );
  }

  function handleAll(): void {
    setSelectedStatuses([]);
  }

  return (
    <section
      className="flex flex-col gap-8"
      data-testid="payments-orders-page"
    >
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('payments_orders.title')}
        </h2>
        <p className="text-sm text-outline">
          {t('payments_orders.subtitle')}
        </p>
      </header>

      {/* ── Filters bar ─────────────────────────────────────────────── */}
      <article
        className="flex flex-col gap-6 rounded-xl bg-surface-container p-6"
        data-testid="payments-orders-filters"
      >
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label={t('payments_orders.filter.status_aria_label')}
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payments_orders.filter.status_label')}
          </span>
          <button
            type="button"
            onClick={handleAll}
            data-testid="payments-filter-status-all"
            aria-pressed={selectedStatuses.length === 0}
            className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container ${
              selectedStatuses.length === 0
                ? 'bg-primary-container text-on-primary'
                : 'bg-surface-container-highest text-on-surface hover:bg-surface-bright'
            }`}
          >
            {t('payments_orders.status.all')}
          </button>
          {ALL_STATUSES.map((s) => {
            const active = selectedStatuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                data-testid={`payments-filter-status-${s}`}
                aria-pressed={active}
                className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container ${
                  active
                    ? 'bg-primary-container text-on-primary'
                    : 'bg-surface-container-highest text-on-surface hover:bg-surface-bright'
                }`}
              >
                {t(STATUS_LABEL_KEY[s])}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('payments_orders.filter.account_id')}
            </span>
            <input
              type="text"
              placeholder={t('payments_orders.filter.account_id.placeholder')}
              value={accountIdInput}
              onChange={(e) => setAccountIdInput(e.currentTarget.value)}
              data-testid="payments-filter-account"
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('payments_orders.filter.from')}
            </span>
            <input
              type="text"
              value={fromInput}
              onChange={(e) => setFromInput(e.currentTarget.value)}
              data-testid="payments-filter-from"
              className={INPUT_CLASS}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('payments_orders.filter.to')}
            </span>
            <input
              type="text"
              value={toInput}
              placeholder={t('payments_orders.filter.to.placeholder')}
              onChange={(e) => setToInput(e.currentTarget.value)}
              data-testid="payments-filter-to"
              className={INPUT_CLASS}
            />
          </label>
        </div>
      </article>

      {/* ── Results table ────────────────────────────────────────────── */}
      {list.isPending ? (
        <p className="text-sm text-outline" data-testid="payments-orders-loading">
          {t('payments_orders.loading_aria')}
        </p>
      ) : null}
      {list.isError ? (
        <p
          className="rounded-xl bg-error-container p-4 text-sm text-on-error-container"
          role="alert"
          data-testid="payments-orders-error"
        >
          {list.error instanceof OpsApiError
            ? `${list.error.code}: ${list.error.message}`
            : t('payments_orders.error.title')}
        </p>
      ) : null}
      {list.data !== undefined && orders.length === 0 ? (
        <p className="text-sm text-outline" data-testid="payments-orders-empty">
          {t('payments_orders.empty')}
        </p>
      ) : null}

      {orders.length > 0 ? (
        <article
          className="flex flex-col gap-4 rounded-xl bg-surface-container p-4"
          data-testid="payments-orders-table"
        >
          <table className="w-full border-separate border-spacing-0">
            <thead>
              <tr className="bg-surface-container-high">
                <Th>{t('payments_orders.column.created')}</Th>
                <Th>{t('payments_orders.column.account')}</Th>
                <Th>{t('payments_orders.column.status')}</Th>
                <Th>{t('payments_orders.column.usd')}</Th>
                <Th>{t('payments_orders.column.cny')}</Th>
                <Th>{t('payments_orders.column.rate')}</Th>
                <Th>{t('payments_orders.column.pay')}</Th>
                <Th>{t('payments_orders.column.paid_at')}</Th>
                <Th>{t('payments_orders.column.order_id')}</Th>
                <Th align="right">{t('payments_orders.column.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((row, i) => {
                const expanded = expandedOrderId === row.order_id;
                return (
                  <OrderRow
                    key={row.order_id}
                    row={row}
                    zebra={i % 2 === 0}
                    expanded={expanded}
                    onToggleExpand={() =>
                      setExpandedOrderId((prev) =>
                        prev === row.order_id ? null : row.order_id,
                      )
                    }
                    onReconcile={() => setReconcileTarget(row)}
                  />
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-outline" data-testid="payments-orders-count">
              {t('payments_orders.showing', { n: String(orders.length) })}
            </span>
            {list.hasNextPage ? (
              <button
                type="button"
                onClick={() => void list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
                data-testid="payments-orders-load-more"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
              >
                {list.isFetchingNextPage
                  ? t('payments_orders.loading_more')
                  : t('payments_orders.load_more')}
              </button>
            ) : null}
          </div>
        </article>
      ) : null}

      {/* ── Reconcile modal ──────────────────────────────────────────── */}
      {reconcileTarget !== null ? (
        <ReconcileModal
          target={reconcileTarget}
          onClose={() => setReconcileTarget(null)}
        />
      ) : null}
    </section>
  );
}

interface ThProps {
  readonly children: React.ReactNode;
  readonly align?: 'left' | 'right';
}

function Th({ children, align = 'left' }: ThProps): JSX.Element {
  return (
    <th
      scope="col"
      className={`px-4 py-3 ${align === 'right' ? 'text-right' : 'text-left'} text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant`}
    >
      {children}
    </th>
  );
}

interface OrderRowProps {
  readonly row: OpsPaymentOrderSummary;
  readonly zebra: boolean;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
  readonly onReconcile: () => void;
}

function OrderRow({
  row,
  zebra,
  expanded,
  onToggleExpand,
  onReconcile,
}: OrderRowProps): JSX.Element {
  const t = useT();
  const variant = STATUS_VARIANT[row.status];
  const accountLabel = row.account_email ?? row.account_id;
  const reconcilable = row.status === 'pending';
  return (
    <>
      <tr
        className={zebra ? 'bg-surface-container' : 'bg-surface-container-low'}
        data-testid={`payments-order-row-${row.order_id}`}
      >
        <td className="px-4 py-3 font-mono text-[12px] text-on-surface">
          {formatTimestamp(row.created_at)}
        </td>
        <td className="px-4 py-3 text-sm">
          <Link
            to={`/customers/accounts/${encodeURIComponent(row.account_id)}`}
            className="text-primary hover:underline"
            data-testid={`payments-order-account-${row.order_id}`}
          >
            {accountLabel}
          </Link>
        </td>
        <td className="px-4 py-3">
          <StatusChip
            variant={variant}
            label={t(STATUS_LABEL_KEY[row.status])}
            testId={`payments-order-status-${row.order_id}`}
          />
        </td>
        <td className="px-4 py-3 font-mono text-[13px] text-on-surface">
          {formatUsdCents(row.amount_usd_cents)}
        </td>
        <td className="px-4 py-3 font-mono text-[13px] text-on-surface">
          {formatCnyCents(row.amount_cny_cents)}
        </td>
        <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant">
          {formatRate(row.applied_rate)}
        </td>
        <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant">
          {row.pay_type}
        </td>
        <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant">
          {formatTimestamp(row.paid_at)}
        </td>
        <td className="px-4 py-3 font-mono text-[12px] text-on-surface-variant">
          {truncateId(row.order_id)}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onToggleExpand}
              data-testid={`payments-order-expand-${row.order_id}`}
              aria-expanded={expanded}
              className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
            >
              {expanded
                ? t('payments_orders.row.collapse')
                : t('payments_orders.row.details')}
            </button>
            <button
              type="button"
              onClick={onReconcile}
              disabled={!reconcilable}
              data-testid={`payments-order-reconcile-${row.order_id}`}
              aria-disabled={!reconcilable}
              title={
                reconcilable
                  ? t('payments_orders.row.reconcile_tooltip.available')
                  : t('payments_orders.row.reconcile_tooltip.disabled')
              }
              className="inline-flex items-center justify-center rounded-lg bg-primary-container px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
            >
              {t('payments_orders.row.reconcile')}
            </button>
          </div>
        </td>
      </tr>
      {expanded ? (
        <tr
          className={zebra ? 'bg-surface-container' : 'bg-surface-container-low'}
          data-testid={`payments-order-detail-${row.order_id}`}
        >
          <td colSpan={10} className="px-4 pb-4">
            <div className="flex flex-col gap-3 rounded-lg bg-surface-container-lowest p-4">
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.order_id')}
                value={row.order_id}
                mono
              />
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.out_trade_no')}
                value={row.out_trade_no}
                mono
              />
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.upstream_trade_no')}
                value={
                  row.upstream_trade_no ??
                  t('payments_orders.reconcile_modal.detail.empty')
                }
                mono
              />
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.expired_at')}
                value={formatTimestamp(row.expired_at)}
                mono
              />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

interface DetailRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function DetailRow({ label, value, mono }: DetailRowProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
        {label}
      </span>
      <span
        className={`text-[12px] ${mono === true ? 'font-mono' : ''} text-on-surface break-all`}
      >
        {value}
      </span>
    </div>
  );
}

interface ReconcileModalProps {
  readonly target: OpsPaymentOrderSummary;
  readonly onClose: () => void;
}

function ReconcileModal({ target, onClose }: ReconcileModalProps): JSX.Element {
  const t = useT();
  const reconcile = useReconcilePaymentOrder();
  const toast = useToast();
  const [note, setNote] = useState('');
  // Typed-confirmation gate — operator must type the order id exactly
  // before the Reconcile button activates. Matches the friction pattern
  // on the balance-adjust modal (AccountDetail). Prevents fat-finger
  // credits on wrong-row clicks.
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = confirmText === target.order_id && !reconcile.isPending;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const result = await reconcile.mutateAsync({
        orderId: target.order_id,
        body: note.trim().length > 0 ? { note: note.trim() } : {},
      });
      if (result.reconciled) {
        toast.success(
          t('payments_orders.reconcile_modal.toast.credited', {
            orderId: target.order_id,
            status: result.newStatus,
          }),
        );
      } else if ('upstreamStatus' in result) {
        toast.info(
          t('payments_orders.reconcile_modal.toast.upstream', {
            orderId: target.order_id,
            status: result.upstreamStatus,
          }),
        );
      } else {
        toast.info(
          t('payments_orders.reconcile_modal.toast.already', {
            orderId: target.order_id,
            status: result.currentStatus,
          }),
        );
      }
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!reconcile.isPending) onClose();
      }}
      titleId="payments-reconcile-title"
      testId="payments-reconcile-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2
          id="payments-reconcile-title"
          className="text-base font-semibold text-on-surface"
        >
          {t('payments_orders.reconcile_modal.title')}
        </h2>
        <p className="text-sm text-outline">
          {t('payments_orders.reconcile_modal.body', {
            orderId: target.order_id,
            amount: formatUsdCents(target.amount_usd_cents),
          })}
        </p>
        <dl className="grid grid-cols-2 gap-3 rounded-lg bg-surface-container-lowest p-4 text-[12px]">
          <dt className="text-outline-variant">
            {t('payments_orders.reconcile_modal.account')}
          </dt>
          <dd className="font-mono text-on-surface break-all">
            {target.account_email ?? target.account_id}
          </dd>
          <dt className="text-outline-variant">
            {t('payments_orders.reconcile_modal.usd')}
          </dt>
          <dd className="font-mono text-on-surface">{formatUsdCents(target.amount_usd_cents)}</dd>
          <dt className="text-outline-variant">
            {t('payments_orders.reconcile_modal.cny')}
          </dt>
          <dd className="font-mono text-on-surface">{formatCnyCents(target.amount_cny_cents)}</dd>
          <dt className="text-outline-variant">
            {t('payments_orders.reconcile_modal.applied_rate')}
          </dt>
          <dd className="font-mono text-on-surface">{formatRate(target.applied_rate)}</dd>
        </dl>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payments_orders.reconcile_modal.note.label')}
          </span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder={t('payments_orders.reconcile_modal.note.placeholder')}
            data-testid="payments-reconcile-note"
            disabled={reconcile.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('payments_orders.reconcile_modal.type_to_confirm')}
          </span>
          <span className="text-[11px] text-outline">
            {t('payments_orders.reconcile_modal.type_hint_prefix')}
            <code className="font-mono text-on-surface">{target.order_id}</code>
            {t('payments_orders.reconcile_modal.type_hint_suffix')}
          </span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder={target.order_id}
            data-testid="payments-reconcile-typed-confirm"
            disabled={reconcile.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="payments-reconcile-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              if (!reconcile.isPending) onClose();
            }}
            disabled={reconcile.isPending}
            data-testid="payments-reconcile-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            data-testid="payments-reconcile-confirm"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {reconcile.isPending
              ? t('payments_orders.reconcile_modal.submitting')
              : t('payments_orders.reconcile_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
